import React, { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "react-toastify";
import { backupServer } from "../../server";
import LoadingComponent from "../../Components/LoadingComponent";
import {
  MdListAlt,
  MdRefresh,
  MdSearch,
  MdContentCopy,
  MdFilterList,
  MdClose,
  MdCheckCircle,
  MdOutlineCalendarToday,
  MdLockOutline,
  MdChevronLeft,
  MdChevronRight,
  MdVerified,
  MdShield,
  MdDns,
} from "react-icons/md";

export default function BackupLogsList() {
  // Authentication & Session state
  const [backupToken, setBackupToken] = useState(
    () => localStorage.getItem("backup_server_jwt_token") || "",
  );
  const [backupUser, setBackupUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Pagination & Query Filter States for GET /api/v1/backups/logs
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceClientFilter, setSourceClientFilter] = useState("all");
  const [clientIpAddressFilter, setClientIpAddressFilter] = useState("");
  const [isScannedFilter, setIsScannedFilter] = useState("all");
  const [isProcessedFilter, setIsProcessedFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("ReceivedAt");
  const [sortDescending, setSortDescending] = useState(true);

  // Response Data state
  const [logsData, setLogsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPagesServer, setTotalPagesServer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Selected Log Inspector Modal state
  const [selectedLog, setSelectedLog] = useState(null);

  // Profile API Function (/api/v1/auth/profile)
  const fetchUserProfile = useCallback(async (authToken) => {
    if (!authToken) return null;
    try {
      const res = await axios.get(`${backupServer}/auth/profile`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 6000,
      });

      if (res.data && res.data.success && res.data.data) {
        setBackupUser(res.data.data);
        return res.data.data;
      }
    } catch (err) {
      console.warn("Failed to fetch profile from /api/v1/auth/profile:", err.message);
      setBackupUser(null);
    }
    return null;
  }, []);

  // Login Function (/api/v1/auth/login)
  const loginToBackupServer = useCallback(async (username, password) => {
    const targetUser = username || loginUsername;
    const targetPass = password || loginPassword;

    if (!targetUser || !targetPass) {
      toast.error("Please enter both username and password.");
      return null;
    }

    setAuthLoading(true);
    try {
      const res = await axios.post(
        `${backupServer}/auth/login`,
        { username: targetUser, password: targetPass },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          timeout: 8000,
        },
      );

      if (res.data && res.data.token) {
        const newToken = res.data.token;
        setBackupToken(newToken);
        localStorage.setItem("backup_server_jwt_token", newToken);

        const userProfile = await fetchUserProfile(newToken);
        if (userProfile) {
          toast.success(`Authenticated as ${userProfile.fullName || targetUser}`);
          setShowLoginModal(false);
          return newToken;
        }
      } else {
        toast.error("Login failed: Invalid credentials");
      }
    } catch (err) {
      toast.error(`Authentication failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setAuthLoading(false);
    }
    return null;
  }, [loginUsername, loginPassword, fetchUserProfile]);

  // Logout Function
  const logoutBackupServer = useCallback(() => {
    localStorage.removeItem("backup_server_jwt_token");
    setBackupToken("");
    setBackupUser(null);
    toast.info("Logged out from Backup Server");
    setShowLoginModal(true);
  }, []);

  // Check Token & Profile on Mount
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("backup_server_jwt_token");
      if (storedToken) {
        setBackupToken(storedToken);
        const profile = await fetchUserProfile(storedToken);
        if (!profile) {
          localStorage.removeItem("backup_server_jwt_token");
          setBackupToken("");
          setBackupUser(null);
          setShowLoginModal(true);
        }
      } else {
        setShowLoginModal(true);
      }
    };
    initAuth();
  }, [fetchUserProfile]);

  // Fetch Backup Logs API (GET /api/v1/backups/logs)
  const fetchBackupLogs = useCallback(
    async (isSilent = false) => {
      const activeToken =
        backupToken || localStorage.getItem("backup_server_jwt_token");
      if (!activeToken) return;

      if (!isSilent) setLoading(true);
      try {
        const queryParams = {
          pageNo,
          pageSize,
          sortBy: sortBy || "ReceivedAt",
          sortDescending: sortDescending,
        };

        if (searchParam && searchParam.trim() !== "") {
          queryParams.searchParam = searchParam.trim();
        }
        if (statusFilter && statusFilter !== "all") {
          queryParams.status = statusFilter;
        }
        if (sourceClientFilter && sourceClientFilter !== "all") {
          queryParams.sourceClient = sourceClientFilter;
        }
        if (clientIpAddressFilter && clientIpAddressFilter.trim() !== "") {
          queryParams.clientIpAddress = clientIpAddressFilter.trim();
        }
        if (isScannedFilter !== "all") {
          queryParams.isScanned = isScannedFilter === "true";
        }
        if (isProcessedFilter !== "all") {
          queryParams.isProcessed = isProcessedFilter === "true";
        }
        if (startDate) {
          queryParams.startDate = startDate;
        }
        if (endDate) {
          queryParams.endDate = endDate;
        }

        const response = await axios.get(`${backupServer}/backups/logs`, {
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${activeToken}`,
          },
          params: queryParams,
          timeout: 10000,
        });

        if (response && response.data) {
          const res = response.data;
          if (res.success && Array.isArray(res.data)) {
            setLogsData(res.data);
            setTotalCount(res.count !== undefined ? res.count : res.data.length);
            if (res.totalPages !== undefined) {
              setTotalPagesServer(res.totalPages);
            } else {
              setTotalPagesServer(null);
            }
          } else if (Array.isArray(res.data)) {
            setLogsData(res.data);
            setTotalCount(res.count || res.data.length);
            setTotalPagesServer(res.totalPages || null);
          } else if (Array.isArray(res)) {
            setLogsData(res);
            setTotalCount(res.length);
            setTotalPagesServer(null);
          } else {
            setLogsData([]);
            setTotalCount(0);
            setTotalPagesServer(1);
          }
          setLastUpdated(new Date());
          if (!isSilent) toast.success("Backup logs loaded successfully");
        }
      } catch (err) {
        console.warn("Error fetching backup logs from GET /api/v1/backups/logs:", err.message);
        if (
          err.response &&
          (err.response.status === 401 || err.response.status === 403)
        ) {
          toast.error("Session expired or unauthorized. Please login again.");
          logoutBackupServer();
          return;
        }
        toast.error(
          `Failed to load logs: ${
            err.response?.data?.message || err.message || "Network Error"
          }`
        );
      } finally {
        if (!isSilent) setLoading(false);
      }
    },
    [
      pageNo,
      pageSize,
      searchParam,
      statusFilter,
      sourceClientFilter,
      clientIpAddressFilter,
      isScannedFilter,
      isProcessedFilter,
      startDate,
      endDate,
      sortBy,
      sortDescending,
      backupToken,
      logoutBackupServer,
    ],
  );

  useEffect(() => {
    if (backupUser && backupToken) {
      fetchBackupLogs();
    }
  }, [
    pageNo,
    pageSize,
    statusFilter,
    sourceClientFilter,
    clientIpAddressFilter,
    isScannedFilter,
    isProcessedFilter,
    startDate,
    endDate,
    sortBy,
    sortDescending,
    backupUser,
    backupToken,
    fetchBackupLogs,
  ]);

  // Derived client options for filter dropdown
  const uniqueClients = useMemo(() => {
    const clients = new Set();
    if (Array.isArray(logsData)) {
      logsData.forEach((log) => {
        if (log.sourceClient) clients.add(log.sourceClient);
      });
    }
    return Array.from(clients);
  }, [logsData]);

  // Total Pages Calculation
  const totalPages =
    totalPagesServer || Math.max(1, Math.ceil(totalCount / pageSize));

  // Check if any filters are active
  const isFilterActive =
    Boolean(startDate) ||
    Boolean(endDate) ||
    Boolean(searchParam) ||
    Boolean(clientIpAddressFilter) ||
    statusFilter !== "all" ||
    sourceClientFilter !== "all" ||
    isScannedFilter !== "all" ||
    isProcessedFilter !== "all";

  // Reset Filters Function
  const resetAllFilters = () => {
    setSearchParam("");
    setStatusFilter("all");
    setSourceClientFilter("all");
    setClientIpAddressFilter("");
    setIsScannedFilter("all");
    setIsProcessedFilter("all");
    setStartDate("");
    setEndDate("");
    setPageNo(1);
  };

  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Helper date formatter
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 text-slate-800 font-sans">
      {/* Header Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-slate-200/80">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-600 text-white rounded-xl shadow-md shadow-blue-500/20">
              <MdListAlt className="text-2xl" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                  Backup Logs & Audit Trail
                </h1>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    backupUser
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-rose-50 text-rose-700 border border-rose-200"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      backupUser ? "bg-emerald-500 animate-pulse" : "bg-rose-500"
                    }`}
                  ></span>
                  {backupUser ? `${totalCount} Total Logs` : "Locked / Auth Required"}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                <span>•</span>
                <span>
                  Page {pageNo} of {totalPages}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Controls: Auth Status, Refresh */}
        <div className="flex flex-wrap items-center gap-3">
          {backupUser ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowLoginModal(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-xl text-xs font-semibold transition-all shadow-sm"
                title="Click to view Backup Server profile session details"
              >
                <MdVerified className="text-sm text-blue-600" />
                <span>Session: {backupUser.fullName || backupUser.username}</span>
              </button>

              <button
                onClick={logoutBackupServer}
                className="px-3 py-2 bg-slate-100 hover:bg-rose-50 text-slate-600 hover:text-rose-700 border border-slate-200 hover:border-rose-200 rounded-xl text-xs font-semibold transition-all"
                title="Logout from Backup Server"
              >
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowLoginModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-600/20 active:scale-95 transition-all"
            >
              <MdLockOutline className="text-base" />
              Login to Backup Server
            </button>
          )}

          {backupUser && (
            <button
              onClick={() => fetchBackupLogs()}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-semibold shadow-md shadow-blue-600/20 hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
            >
              <MdRefresh className={`text-base ${loading ? "animate-spin" : ""}`} />
              Refresh Logs
            </button>
          )}
        </div>
      </div>

      {loading && <LoadingComponent />}

      {!backupUser ? (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 md:p-12 rounded-3xl border border-slate-200 shadow-sm text-center max-w-2xl mx-auto space-y-5 my-8"
        >
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto border border-blue-100 shadow-inner">
            <MdLockOutline className="text-3xl" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              Backup Logs Access Locked
            </h2>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
              Authentication is required to view full audit logs, storage paths, and SHA-256 cryptographic checksums.
            </p>
          </div>

          <button
            onClick={() => setShowLoginModal(true)}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/20 active:scale-95 inline-flex items-center gap-2"
          >
            <MdLockOutline className="text-base" />
            Login to Access Backup Logs
          </button>
        </motion.div>
      ) : (
        <>
          {/* Filter Toolbar for GET /api/v1/backups/logs */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Left Group: Search Bar & Core Filters */}
              <div className="flex flex-wrap items-center gap-2.5 text-xs">
                {/* Search Parameter */}
                <div className="relative w-60">
                  <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base" />
                  <input
                    type="text"
                    placeholder="Search logs, checksum, file..."
                    value={searchParam}
                    onChange={(e) => setSearchParam(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setPageNo(1);
                        fetchBackupLogs();
                      }
                    }}
                    className="w-full pl-9 pr-7 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all"
                  />
                  {searchParam && (
                    <button
                      onClick={() => {
                        setSearchParam("");
                        setPageNo(1);
                      }}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <MdClose className="text-sm" />
                    </button>
                  )}
                </div>

                {/* Status Filter */}
                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200 text-slate-700">
                  <MdFilterList className="text-blue-600 text-base shrink-0" />
                  <span className="text-slate-400 font-medium">Status:</span>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPageNo(1);
                    }}
                    className="bg-transparent font-semibold text-slate-800 focus:outline-none cursor-pointer"
                  >
                    <option value="all">All Statuses</option>
                    <option value="Completed">Completed</option>
                    <option value="Infected">Infected</option>
                    <option value="Failed">Failed</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>

                {/* Source Client Filter */}
                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200 text-slate-700">
                  <MdDns className="text-blue-600 text-sm shrink-0" />
                  <span className="text-slate-400 font-medium">Client:</span>
                  <select
                    value={sourceClientFilter}
                    onChange={(e) => {
                      setSourceClientFilter(e.target.value);
                      setPageNo(1);
                    }}
                    className="bg-transparent font-semibold text-slate-800 focus:outline-none cursor-pointer max-w-[130px] truncate"
                  >
                    <option value="all">All Clients</option>
                    {uniqueClients.map((client, idx) => (
                      <option key={idx} value={client}>
                        {client}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Client IP Address Filter */}
                <div className="flex items-center gap-1 bg-slate-50 px-2.5 py-1.5 rounded-xl border border-slate-200 text-slate-700 w-44">
                  <span className="text-slate-400 font-medium shrink-0">IP:</span>
                  <input
                    type="text"
                    placeholder="e.g. 80.13"
                    value={clientIpAddressFilter}
                    onChange={(e) => {
                      setClientIpAddressFilter(e.target.value);
                      setPageNo(1);
                    }}
                    className="bg-transparent text-slate-800 font-semibold focus:outline-none w-full text-xs placeholder-slate-400"
                  />
                  {clientIpAddressFilter && (
                    <button
                      onClick={() => {
                        setClientIpAddressFilter("");
                        setPageNo(1);
                      }}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <MdClose className="text-xs" />
                    </button>
                  )}
                </div>

                {/* Security Scanned Filter */}
                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200 text-slate-700">
                  <MdShield className="text-blue-600 text-sm shrink-0" />
                  <span className="text-slate-400 font-medium">Scanned:</span>
                  <select
                    value={isScannedFilter}
                    onChange={(e) => {
                      setIsScannedFilter(e.target.value);
                      setPageNo(1);
                    }}
                    className="bg-transparent font-semibold text-slate-800 focus:outline-none cursor-pointer"
                  >
                    <option value="all">All</option>
                    <option value="true">Scanned</option>
                    <option value="false">Unscanned</option>
                  </select>
                </div>

                {/* Processed Filter */}
                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200 text-slate-700">
                  <span className="text-slate-400 font-medium">Processed:</span>
                  <select
                    value={isProcessedFilter}
                    onChange={(e) => {
                      setIsProcessedFilter(e.target.value);
                      setPageNo(1);
                    }}
                    className="bg-transparent font-semibold text-slate-800 focus:outline-none cursor-pointer"
                  >
                    <option value="all">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                {/* Date Range Picker */}
                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-700">
                  <MdOutlineCalendarToday className="text-blue-600 text-sm shrink-0" />
                  <span className="text-slate-400 font-medium">Date:</span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPageNo(1);
                    }}
                    className="bg-transparent text-slate-800 font-semibold focus:outline-none cursor-pointer w-28 text-xs"
                  />
                  <span className="text-slate-400 font-medium">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPageNo(1);
                    }}
                    className="bg-transparent text-slate-800 font-semibold focus:outline-none cursor-pointer w-28 text-xs"
                  />
                </div>
              </div>

              {/* Right Group: Sort Controls & Clear */}
              <div className="flex items-center gap-2.5 text-xs">
                {/* Sort By Dropdown */}
                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200 text-slate-700">
                  <span className="text-slate-400 font-medium">Sort:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-transparent font-semibold text-slate-800 focus:outline-none cursor-pointer"
                  >
                    <option value="ReceivedAt">Received Time</option>
                    <option value="FileSizeBytes">File Size</option>
                    <option value="FileName">File Name</option>
                    <option value="Status">Status</option>
                    <option value="ScannedAt">Scanned Time</option>
                    <option value="ProcessedAt">Processed Time</option>
                  </select>
                  <button
                    onClick={() => setSortDescending(!sortDescending)}
                    className="px-2 py-0.5 bg-white border border-slate-200 rounded-lg text-[10px] font-bold text-blue-600 hover:bg-blue-50 transition-colors"
                    title="Toggle Sort Order"
                  >
                    {sortDescending ? "DESC" : "ASC"}
                  </button>
                </div>

                {/* Clear Filters Button */}
                {isFilterActive && (
                  <button
                    onClick={resetAllFilters}
                    className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-xl font-semibold transition-all"
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Backup Logs Data Table */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-500 font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Log ID</th>
                    <th className="py-3 px-4">File Name</th>
                    <th className="py-3 px-4">File Size</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Source Client / IP</th>
                    <th className="py-3 px-4">Security Scanned</th>
                    <th className="py-3 px-4">Processed</th>
                    <th className="py-3 px-4">Received Time</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {logsData.length > 0 ? (
                    logsData.map((log) => {
                      const displayFileName =
                        log.fileName && log.fileName.length > 40
                          ? `...${log.fileName.slice(-35)}`
                          : log.fileName;

                      return (
                        <tr
                          key={log.id}
                          className="hover:bg-blue-50/40 transition-colors cursor-pointer"
                          onClick={() => setSelectedLog(log)}
                        >
                          <td className="py-3 px-4 font-mono font-medium text-blue-700">
                            #{log.id}
                          </td>
                          <td className="py-3 px-4 font-mono font-medium text-slate-900">
                            <span title={log.fileName}>{displayFileName}</span>
                          </td>
                          <td className="py-3 px-4 font-bold text-slate-800">
                            {log.fileSizeFormatted ||
                              `${(log.fileSizeBytes / (1024 * 1024)).toFixed(2)} MB`}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                                log.status === "Completed"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : log.status === "Infected"
                                    ? "bg-rose-50 text-rose-700 border-rose-200"
                                    : "bg-amber-50 text-amber-700 border-amber-200"
                              }`}
                            >
                              <MdCheckCircle className="text-xs" />
                              {log.status || "Completed"}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-mono text-slate-600">
                            {log.sourceClient || log.clientIpAddress ? (
                              <span>
                                {log.sourceClient || "—"}
                                {log.clientIpAddress && log.clientIpAddress !== log.sourceClient
                                  ? ` (${log.clientIpAddress})`
                                  : ""}
                              </span>
                            ) : (
                              "Local"
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {log.isScanned ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                                <MdCheckCircle /> Clean Scanned
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-slate-400">
                                Unscanned
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {log.isProcessed ? (
                              <span className="inline-flex items-center gap-1 text-blue-600 font-semibold">
                                <MdCheckCircle /> Processed
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-slate-400">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-500">
                            {formatDate(log.receivedAt)}
                          </td>
                          <td
                            className="py-3 px-4 text-right flex items-center justify-end gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => setSelectedLog(log)}
                              className="px-3 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg font-semibold text-[11px] transition-colors"
                            >
                              Inspect Log
                            </button>
                            {log.checksumSHA256 && (
                              <button
                                onClick={() => copyToClipboard(log.checksumSHA256)}
                                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Copy SHA-256 Checksum Hash"
                              >
                                <MdContentCopy className="text-sm" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="py-12 text-center text-slate-400 text-xs">
                        No backup logs found matching current filter parameters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Server-Side Pagination Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-4 text-slate-500">
                <span>
                  Showing {logsData.length > 0 ? (pageNo - 1) * pageSize + 1 : 0} to{" "}
                  {Math.min(pageNo * pageSize, totalCount)} of {totalCount} total entries
                </span>

                {/* Page Size Selector */}
                <div className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-xl border border-slate-200 text-slate-600 font-medium">
                  <span>Rows:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPageNo(1);
                    }}
                    className="bg-transparent font-bold text-blue-700 focus:outline-none cursor-pointer"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
                  disabled={pageNo === 1}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1 transition-all shadow-sm"
                >
                  <MdChevronLeft className="text-base" /> Previous
                </button>

                <span className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl font-bold font-mono">
                  {pageNo} / {totalPages}
                </span>

                <button
                  onClick={() => setPageNo((prev) => Math.min(prev + 1, totalPages))}
                  disabled={pageNo >= totalPages}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1 transition-all shadow-sm"
                >
                  Next <MdChevronRight className="text-base" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* LOG INSPECTOR MODAL */}
      <AnimatePresence>
        {selectedLog && (
          <div className="fixed inset-0 z-50 bg-slate-800/40 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col"
            >
              {/* Header */}
              <div className="p-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-md">
                    <MdListAlt className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white">
                      Backup Log Details #{selectedLog.id}
                    </h3>
                    <p className="text-xs text-blue-100 font-mono">
                      {selectedLog.fileName}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="p-1.5 text-blue-100 hover:text-white hover:bg-white/20 rounded-xl transition-all"
                >
                  <MdClose className="text-xl" />
                </button>
              </div>

              {/* Details Body */}
              <div className="p-6 space-y-4 text-xs text-slate-700 max-h-[75vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                    <span className="text-slate-400 block font-medium">Status</span>
                    <span className="font-bold text-slate-900 text-sm">
                      {selectedLog.status || "Completed"}
                    </span>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                    <span className="text-slate-400 block font-medium">File Size</span>
                    <span className="font-bold text-purple-700 text-sm">
                      {selectedLog.fileSizeFormatted ||
                        `${(selectedLog.fileSizeBytes / (1024 * 1024)).toFixed(2)} MB`}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <label className="block text-slate-500 font-semibold">
                    Original File Path
                  </label>
                  <div className="p-3 bg-slate-100 rounded-xl font-mono text-[11px] text-slate-800 break-all border border-slate-200">
                    {selectedLog.originalFilePath || "N/A"}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-slate-500 font-semibold">
                    Server Storage Path
                  </label>
                  <div className="p-3 bg-slate-100 rounded-xl font-mono text-[11px] text-slate-800 break-all border border-slate-200">
                    {selectedLog.storagePath || "N/A"}
                  </div>
                </div>

                {selectedLog.checksumSHA256 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-slate-500 font-semibold">
                        SHA-256 Checksum Hash
                      </label>
                      <button
                        onClick={() => copyToClipboard(selectedLog.checksumSHA256)}
                        className="text-blue-600 hover:text-blue-700 font-semibold text-[11px] flex items-center gap-1"
                      >
                        <MdContentCopy /> Copy Hash
                      </button>
                    </div>
                    <div className="p-3 bg-blue-50/60 rounded-xl font-mono text-[11px] text-blue-900 break-all border border-blue-200">
                      {selectedLog.checksumSHA256}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100 text-[11px]">
                  <div>
                    <span className="text-slate-400 block font-medium">Source Client</span>
                    <span className="font-semibold text-slate-800 font-mono">
                      {selectedLog.sourceClient || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-medium">Client IP Address</span>
                    <span className="font-semibold text-slate-800 font-mono">
                      {selectedLog.clientIpAddress || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-medium">Security Scanned</span>
                    <span className="font-semibold text-slate-800">
                      {selectedLog.isScanned ? `Clean (${formatDate(selectedLog.scannedAt)})` : "Unscanned"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-medium">Processed</span>
                    <span className="font-semibold text-slate-800">
                      {selectedLog.isProcessed ? `Completed (${formatDate(selectedLog.processedAt)})` : "Pending"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-medium">Received At</span>
                    <span className="font-semibold text-slate-800">
                      {formatDate(selectedLog.receivedAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                <button
                  onClick={() => setSelectedLog(null)}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/20"
                >
                  Close Inspector
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* LOGIN MODAL */}
      <AnimatePresence>
        {showLoginModal && (
          <div className="fixed inset-0 z-50 bg-slate-800/40 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-slate-200"
            >
              <div className="p-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-md">
                    <MdLockOutline className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white">
                      Backup Server Authentication
                    </h3>
                    <p className="text-sm text-blue-100">Secure Login</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowLoginModal(false)}
                  className="p-1.5 text-blue-100 hover:text-white hover:bg-white/20 rounded-xl transition-all"
                >
                  <MdClose className="text-xl" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  loginToBackupServer(loginUsername, loginPassword);
                }}
                className="p-5 space-y-4 text-xs"
              >
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">
                    Username / Email
                  </label>
                  <input
                    type="text"
                    required
                    value={loginUsername}
                    onChange={(e) => setLoginUsername(e.target.value)}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl font-sans text-base focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    placeholder="Enter username or email"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl font-sans text-base focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    placeholder="Enter password"
                  />
                </div>

                <div className="pt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowLoginModal(false)}
                    className="px-4 py-3 border border-gray-300 text-base bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={authLoading}
                    className="px-5 py-3 text-base bg-blue-600 border border-blue-700 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-md shadow-blue-600/20 flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {authLoading ? (
                      <MdRefresh className="animate-spin text-sm" />
                    ) : (
                      <MdVerified className="text-sm" />
                    )}
                    {authLoading ? "Authenticating..." : "Login & Access Logs"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

